import React from "react";
import { ArrowRight, CheckCircle2, ChevronRight } from "lucide-react";
import { euro } from "../practices/AdvancePanel";
import { formatDate } from "../../lib/practiceApi";

const whenLabel = days => {
  if (days == null) return "Da pianif.";
  if (days < 0) return `${Math.abs(days)} gg fa`;
  if (days === 0) return "Oggi";
  if (days === 1) return "Domani";
  return `Tra ${days} gg`;
};
const tone = days => (days == null ? "calm" : days < 0 ? "late" : days <= 1 ? "" : "calm");

export const NextActionsCard = ({ rows = [], onOpen, onAll }) => <section className="cr-card wide" data-testid="cr-next-actions">
  <header><h3>Prossime azioni</h3><button onClick={onAll} data-testid="cr-next-actions-all">Vedi tutte <ArrowRight size={13} /></button></header>
  {rows.length ? rows.map(row => <button className="cr-row" key={`${row.practice_id}-${row.text}`} data-testid={`cr-next-action-${row.practice_id}`}
    onClick={() => onOpen(row.practice_id)}>
    <span className={`cr-when ${tone(row.days)}`}>{whenLabel(row.days)}</span>
    <span className={`cr-dot ${tone(row.days)}`} />
    <span style={{ flex: 1, minWidth: 0 }}><strong>{row.text}</strong><small>{row.practice}</small><small className={`cr-client ${row.client ? "" : "missing"}`} data-testid={`cr-next-action-client-${row.practice_id}`}>Committente: {row.client || "da definire"}</small></span>
    <ChevronRight size={16} className="chev" />
  </button>) : <p className="cr-tagline" data-testid="cr-next-actions-empty"><CheckCircle2 size={14} />Nessuna azione dello studio in sospeso</p>}
</section>;

export const WaitingCard = ({ waiting, onOpen }) => <section className="cr-card" data-testid="cr-waiting">
  <header><h3>In attesa esterna</h3><b style={{ fontSize: 15, fontFamily: "var(--display)", color: "var(--navy)" }} data-testid="cr-waiting-total">{waiting?.total || 0}</b></header>
  {waiting?.by_party?.length ? waiting.by_party.map(item => <button className="cr-row" key={item.party}
    data-testid={`cr-waiting-${item.party}`} onClick={() => onOpen(item.party)}>
    <span className="cr-dot calm" />
    <span style={{ flex: 1 }}><strong>{item.party}</strong><small>in attesa di riscontro</small></span><b>{item.count}</b>
    <ChevronRight size={16} className="chev" />
  </button>) : <p className="cr-tagline" data-testid="cr-waiting-empty"><CheckCircle2 size={14} />Nessuna pratica in attesa esterna</p>}
</section>;

export const EconomicsCard = ({ engaged, toInvoice, invoiced, outstanding, onOpen }) => <section className="cr-card" data-testid="cr-economics">
  <header><h3>Situazione economica</h3><button onClick={onOpen} data-testid="cr-economics-open">Apri economia <ArrowRight size={13} /></button></header>
  <div className="cr-money">
    <div><span>Incaricato</span><strong data-testid="cr-eco-engaged">{euro(engaged)}</strong></div>
    <div><span>Da fatturare maturato</span><strong data-testid="cr-eco-to-invoice">{euro(toInvoice)}</strong></div>
    <div><span>Fatturato</span><strong data-testid="cr-eco-invoiced">{euro(invoiced)}</strong></div>
    <div><span>Da incassare</span><strong data-testid="cr-eco-outstanding">{euro(outstanding)}</strong></div>
  </div>
</section>;

export const AnomaliesCard = ({ rows = [], total = 0, onOpen, onAll }) => <section className="cr-card" data-testid="cr-anomalies">
  <header><h3>Da controllare</h3>{total > rows.length && <button onClick={onAll} data-testid="cr-anomalies-all">Vedi tutte ({total}) <ArrowRight size={13} /></button>}</header>
  {rows.length ? rows.map((row, index) => <button className="cr-row" key={`${row.kind}-${row.practice_id || index}`}
    data-testid={`cr-anomaly-${index}`} onClick={() => row.practice_id ? onOpen(row.practice_id) : onAll()}>
    <span className="cr-dot late" />
    <span style={{ flex: 1, minWidth: 0 }}><strong>{row.kind}</strong><small>{row.practice}{row.detail ? ` · ${row.detail}` : ""}</small></span>
    <ChevronRight size={16} className="chev" />
  </button>) : <p className="cr-tagline" data-testid="cr-anomalies-empty"><CheckCircle2 size={14} />Nessuna anomalia da segnalare</p>}
</section>;

export const WorkloadCard = ({ rows = [] }) => {
  const max = Math.max(...rows.map(row => row.count), 1);
  return <section className="cr-card" data-testid="cr-workload">
    <header><h3>Carico studio</h3></header>
    <div className="cr-bars">
      {rows.map(row => <div key={row.label}><b>{row.count}</b><i style={{ height: `${Math.max((row.count / max) * 46, 4)}px` }} />{row.label}</div>)}
    </div>
  </section>;
};

export const ListView = ({ rows = [], onSelect }) => <div className="app-grid-scroll" data-testid="cr-list-scroll"><table className="cr-list" data-testid="cr-list-view">
  <thead><tr><th>Pratica</th><th>Stato</th><th>Prossima azione</th><th>Scadenza</th><th>Committente</th><th>Responsabile</th><th>Economia</th></tr></thead>
  <tbody>
    {rows.map(row => <tr key={row.id} data-testid={`cr-list-row-${row.id}`} onClick={() => onSelect(row)}>
      <td><strong>{row.name}</strong><small>{row.type_label}</small></td>
      <td>{row.status}<small>{row.progress}%</small>{row.catasto_attivo && <small data-testid={`cr-list-catasto-${row.id}`}>CATASTO · {row.stato_catasto}</small>}</td>
      <td>{row.next_action.text}<small>{row.owner === "studio" ? "Studio" : `Attesa ${row.party || "esterna"}`}</small>{row.has_external_waits && <small data-testid={`cr-list-waits-${row.id}`}>{row.external_waits.map(w => `${w.party} · ${w.waiting_days == null ? "inizio non indicato" : `${w.waiting_days} gg`}`).join(" / ")}</small>}</td>
      <td>{row.nearest_date ? formatDate(row.nearest_date) : "—"}<small>{row.nearest_label}</small></td>
      <td>{row.client_name || "—"}</td>
      <td>{row.responsible || "Da assegnare"}</td>
      <td>{euro(row.economics.to_invoice)}<small>da fatturare</small></td>
    </tr>)}
  </tbody>
</table></div>;
